import { Row } from "@faire/web--source/slate/Layout";
import { TooltipTypography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackRetailerPosPdpInventoryCountView } from "@faire/web-api--source/events/retailerPos/view/pdpInventoryCount";
import { trackRetailerPosPdpInventoryTooltipView } from "@faire/web-api--source/events/retailerPos/view/pdpInventoryTooltip";
import { IGetProductOptionInventoryRequest } from "@faire/web-api--source/indigofair/retailers/pos/IGetProductOptionInventoryRequest";
import { useQuery_escape_hatch as useInventoryQuery } from "@faire/web-api--source/retailers-pos-api/product-option-inventory/post-hooks";
import React, { useEffect, useMemo } from "react";

import { POSLogo } from "@faire/retailer/features/PointOfSale/components/POSLogo";
import { Tooltip } from "@faire/retailer/features/PointOfSale/components/Tooltip";
import { usePOSSummary } from "@faire/retailer/features/PointOfSale/hooks/usePOSSummary";
import {
  useAssignSettingPDPInventory,
  useSettingPDPInventory,
} from "@faire/retailer/settings/useSettingPDPInventory";

import { TooltipContent } from "./TooltipContent";

interface IProps {
  optionToken: string;
}

export const ProductOptionPOSInventory: React.FC<IProps> = ({
  optionToken,
}) => {
  const showInventory = useSettingPDPInventory();
  const assignPDPInventory = useAssignSettingPDPInventory();
  const { type, name: posCompanyName, hasLinkedPOS } = usePOSSummary();
  // TODO(Fabio): remove these events and forwarded refs when removing PDP_INVENTORY_COUNTS_RELAUNCH_3_250107
  const inventoryViewRef = useDoActionWhenVisible(() =>
    trackRetailerPosPdpInventoryCountView(optionToken)
  );
  const tooltipViewRef = useDoActionWhenVisible(() =>
    trackRetailerPosPdpInventoryTooltipView(optionToken)
  );

  const { data } = useInventoryQuery(
    IGetProductOptionInventoryRequest.build({
      product_option_tokens: [optionToken],
    }),
    { enabled: hasLinkedPOS }
  );

  const inventoryCountNumber = useMemo(
    () => data?.inventory_data_by_product_option[optionToken]?.total_inventory,
    [data, optionToken]
  );

  useEffect(() => {
    if (inventoryCountNumber !== undefined) {
      assignPDPInventory();
    }
  }, [inventoryCountNumber, assignPDPInventory]);

  if (!showInventory || !hasLinkedPOS || inventoryCountNumber === undefined) {
    return null;
  }

  return (
    <Tooltip
      target={
        <Row align="center" gap={4} ref={inventoryViewRef}>
          <POSLogo type={type} />
          <TooltipTypography style={{ cursor: "default" }}>
            {inventoryCountNumber > 99 ? (
              <StrictLocalMsg
                defaultMessage="99+ in {posCompanyName}"
                description={{
                  text: "Indicates the current inventory for a given product when it exceeds 99 items, refer to image",
                  img: "https://cdn.faire.com/fastly/35a6c7c3b951673ea73078e7ccc718a53472335c8770b9ec75465f4ffa1f6892.png",
                }}
                values={{ posCompanyName }}
              />
            ) : (
              <StrictLocalMsg
                defaultMessage="{inventoryCountNumber, plural, one {# in {posCompanyName}} other {# in {posCompanyName}}}"
                description={{
                  text: "Indicates the current inventory for a given product, refer to image",
                  img: "https://cdn.faire.com/fastly/8ab923a0eda49e668f5ce3c6d94efaa196fab5f8ab4e40409825c1d5cf6fbab6.png",
                }}
                values={{ inventoryCountNumber, posCompanyName }}
              />
            )}
          </TooltipTypography>
        </Row>
      }
    >
      <TooltipContent ref={tooltipViewRef} />
    </Tooltip>
  );
};
